import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { spaceContent } from '../../mocks/content';
import { Link } from 'react-router-dom';

export default function SpacePage() {
  const spaces = [
    {
      ...spaceContent.cafe,
      image: "https://public.readdy.ai/ai/img_res/02640b28d6771590f660c569c1f26f13.jpg"
    },
    {
      ...spaceContent.pilates,
      image: "https://readdy.ai/api/search-image?query=Modern%20reformer%20pilates%20studio%20interior%20with%20multiple%20reformer%20beds%20in%20rows%20natural%20wood%20floors%20soft%20natural%20lighting%20warm%20neutral%20color%20palette%20minimal%20design%20calm%20wellness%20space%20premium%20equipment%20clean%20lines%20generous%20white%20space%20editorial%20photography%20style&width=1200&height=800&seq=nu014&orientation=landscape"
    },
    {
      ...spaceContent.thermal,
      image: "https://readdy.ai/api/search-image?query=Luxury%20private%20thermal%20spa%20suite%20interior%20with%20wooden%20sauna%20door%20natural%20stone%20surfaces%20soft%20ambient%20lighting%20steam%20rising%20warm%20blue-grey%20tones%20minimal%20design%20premium%20wellness%20sanctuary%20linen%20robes%20hanging%20candles%20editorial%20photography%20style%20serene%20atmosphere&width=1200&height=800&seq=nu015&orientation=landscape"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden pt-24">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=minimalist%20wellness%20studio%20interior%20with%20soft%20natural%20light%20streaming%20through%20large%20windows%20clean%20white%20walls%20wooden%20floors%20plants%20serene%20atmosphere%20modern%20architecture%20calm%20peaceful%20space%20neutral%20tones%20simple%20elegant%20design%20professional%20photography&width=1920&height=1080&seq=space-hero-bg-v2&orientation=landscape"
            alt="The Space"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/15 to-black/25"></div>
        </div>

        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 tracking-tight">
            The Space
          </h1>
          <p className="text-lg md:text-xl text-white/95 font-light leading-relaxed">
            Every detail designed to support your practice — from the lighting to the temperature, the materials to the music.
          </p>
        </div>
      </section>

      {/* Design Philosophy Section */}
      <section className="py-24 px-6 md:px-12 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-reset-text mb-6">
                Intentional Design
              </h2>
              <p className="text-base text-reset-text-secondary mb-6 leading-relaxed">
                RE:SET is more than a studio — it's a sanctuary. We've carefully considered every element to create an environment that supports your wellness journey.
              </p>
              <p className="text-base text-reset-text-secondary mb-6 leading-relaxed">
                Natural light floods the space through floor-to-ceiling windows. The temperature is precisely controlled. The materials — wood, stone, linen — are chosen for their warmth and tactility. Even the music is curated to enhance your practice without overwhelming your senses.
              </p>
              <p className="text-base text-reset-text-secondary leading-relaxed">
                This is a space designed for presence, for focus, for the kind of deep work that transforms not just your body, but your relationship to it.
              </p>
            </div>
            <div className="relative h-[500px] rounded-lg overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=modern%20wellness%20studio%20interior%20with%20natural%20light%20wooden%20floors%20plants%20clean%20minimal%20aesthetic%20serene%20atmosphere%20professional%20photography%20neutral%20tones%20calm%20space%20high%20end%20fitness%20studio%20simple%20elegant%20design&width=800&height=1000&seq=space-design-v2&orientation=portrait"
                alt="Studio Design"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-6 md:px-12 bg-reset-bg-alt">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-reset-text mb-6">
              Space Features
            </h2>
            <p className="text-base text-reset-text-secondary max-w-2xl mx-auto">
              Every element is designed to support your practice.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-10">
            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-reset-sage-light rounded-full flex items-center justify-center">
                  <i className="ri-checkbox-circle-line text-2xl text-reset-sage w-6 h-6 flex items-center justify-center"></i>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-reset-text mb-2">
                  Natural Light
                </h3>
                <p className="text-sm text-reset-text-secondary leading-relaxed">
                  Floor-to-ceiling windows bring the outside in, creating a connection to nature and supporting your circadian rhythm.
                </p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-reset-sage-light rounded-full flex items-center justify-center">
                  <i className="ri-checkbox-circle-line text-2xl text-reset-sage w-6 h-6 flex items-center justify-center"></i>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-reset-text mb-2">
                  Climate Control
                </h3>
                <p className="text-sm text-reset-text-secondary leading-relaxed">
                  Precisely regulated temperature and humidity ensure optimal conditions for movement and recovery.
                </p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-reset-sage-light rounded-full flex items-center justify-center">
                  <i className="ri-checkbox-circle-line text-2xl text-reset-sage w-6 h-6 flex items-center justify-center"></i>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-reset-text mb-2">
                  Natural Materials
                </h3>
                <p className="text-sm text-reset-text-secondary leading-relaxed">
                  Wood, stone, and linen create a warm, tactile environment that feels grounding and restorative.
                </p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-reset-sage-light rounded-full flex items-center justify-center">
                  <i className="ri-checkbox-circle-line text-2xl text-reset-sage w-6 h-6 flex items-center justify-center"></i>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-reset-text mb-2">
                  Curated Soundscape
                </h3>
                <p className="text-sm text-reset-text-secondary leading-relaxed">
                  Carefully selected music and acoustics enhance your practice without overwhelming your senses.
                </p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-reset-sage-light rounded-full flex items-center justify-center">
                  <i className="ri-checkbox-circle-line text-2xl text-reset-sage w-6 h-6 flex items-center justify-center"></i>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-reset-text mb-2">
                  Premium Equipment
                </h3>
                <p className="text-sm text-reset-text-secondary leading-relaxed">
                  State-of-the-art Reformer Pilates machines, infrared saunas, and cold plunge pools from leading manufacturers.
                </p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-reset-sage-light rounded-full flex items-center justify-center">
                  <i className="ri-checkbox-circle-line text-2xl text-reset-sage w-6 h-6 flex items-center justify-center"></i>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-reset-text mb-2">
                  Thoughtful Amenities
                </h3>
                <p className="text-sm text-reset-text-secondary leading-relaxed">
                  Luxurious changing rooms, premium toiletries, and all the essentials you need for your practice.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-24 px-6 md:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-reset-text mb-6">
              Explore the Space
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="relative h-96 rounded-lg overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=modern%20reformer%20pilates%20studio%20with%20equipment%20natural%20light%20wooden%20floors%20clean%20minimal%20aesthetic%20serene%20atmosphere%20professional%20photography%20neutral%20tones%20calm%20space%20high%20end%20fitness%20studio&width=800&height=600&seq=space-gallery-1-v2&orientation=landscape"
                alt="Pilates Studio"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="relative h-96 rounded-lg overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=luxury%20thermal%20spa%20suite%20with%20sauna%20natural%20materials%20wood%20stone%20soft%20lighting%20serene%20wellness%20space%20high%20end%20spa%20interior%20calm%20peaceful%20atmosphere%20neutral%20tones%20simple%20elegant&width=800&height=600&seq=space-gallery-2-v2&orientation=landscape"
                alt="Thermal Suite"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="relative h-96 rounded-lg overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=minimalist%20wellness%20cafe%20interior%20with%20natural%20light%20wooden%20tables%20plants%20clean%20white%20aesthetic%20modern%20coffee%20shop%20serene%20atmosphere%20neutral%20tones%20simple%20elegant%20design%20professional%20photography&width=800&height=600&seq=space-gallery-3-v2&orientation=landscape"
                alt="Café"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="relative h-96 rounded-lg overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=luxury%20wellness%20studio%20changing%20room%20with%20natural%20materials%20wooden%20lockers%20plants%20soft%20lighting%20clean%20minimal%20aesthetic%20high%20end%20spa%20amenities%20neutral%20tones%20calm%20atmosphere&width=800&height=600&seq=space-gallery-4-v2&orientation=landscape"
                alt="Amenities"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 md:px-12 bg-reset-blue-light">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-reset-text mb-6">
            Experience the Space
          </h2>
          <p className="text-base text-reset-text-secondary mb-10">
            Book a complimentary intro session and see the difference intentional design makes.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-reset-sage text-white hover:bg-reset-sage-hover px-10 py-4 rounded-full text-sm font-semibold tracking-widest transition-all duration-300 whitespace-nowrap cursor-pointer uppercase shadow-lg hover:shadow-xl"
          >
            BOOK YOUR VISIT
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}